import type { StatusTone } from '@orbind/tokens'
import { Badge, Skeleton } from '@orbind/ui'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../lib/api.ts'
import { useBootstrap } from '../lib/catalog.ts'
import { useT } from '../lib/i18n.tsx'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import './explorer-lists.css'

/**
 * Árvore do portfólio dentro do explorer do SHELL.
 *
 * No protótipo o portfólio nao tem "tela da árvore": a árvore e o explorer
 * permanente ao lado do detalhe (`_shell.html:298-312`). Por isso este
 * componente e montado tanto por `/portfolio/tree` quanto por `/portfolio/:id`
 * — o que muda entre as duas rotas e so o que aparece no `<main>`.
 */

export const LEVEL_ORDER = ['SOLUTION', 'PRODUCT', 'MODULE', 'FEATURE', 'ACTION'] as const
export type PortfolioLevelCode = (typeof LEVEL_ORDER)[number]

export const LEVEL_ICON: Record<string, string> = {
  SOLUTION: 'fas fa-cube',
  PRODUCT: 'fas fa-box',
  MODULE: 'fas fa-th-large',
  FEATURE: 'fas fa-puzzle-piece',
  ACTION: 'fas fa-bolt',
}

/**
 * Chip do explorer -> status do portfólio.
 *
 * Os chips vem do protótipo (`_shell.html:2282-2290`) e os status sao catálogo
 * cadastrável; o mapeamento e por CODE semeado, nunca por posicao na lista.
 * "Em risco" cai em PAUSED: e o único status semeado que sinaliza problema.
 */
const CHIP_STATUS: Record<string, string> = {
  'Em andamento': 'IN_PROGRESS',
  'Em risco': 'PAUSED',
  Planejados: 'PLANNED',
}

export type PortfolioTreeNode = {
  id: string
  parentId: string | null
  level: string
  code: string
  name: string
  depth: number
  portfolioStatusId: string | null
}

/** Árvore completa do portfólio — compartilhada pelas duas rotas. */
export function usePortfolioTree() {
  return useQuery({
    queryKey: ['portfolio', 'tree'],
    queryFn: async () => (await api.portfolio.tree.$get()).json(),
  })
}

/** Traduz a chave do catálogo; sem tradução, devolve a própria chave. */
export function useCatalogLabel() {
  const t = useT()
  return (key: string | null | undefined, fallback: string) => {
    if (!key) return fallback
    const value = t(key)
    return value === key ? fallback : value
  }
}

export function PortfolioExplorer({ selectedId, onCreate }: { selectedId?: string; onCreate: () => void }) {
  const navigate = useNavigate()
  const t = useT()
  const bootstrap = useBootstrap()
  const { search, chip, view } = useExplorer()
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())

  const tree = usePortfolioTree()
  const nodes = useMemo(() => (tree.data?.nodes ?? []) as PortfolioTreeNode[], [tree.data])

  const statusById = useMemo(
    () => new Map((bootstrap.data?.portfolioStatuses ?? []).map((s) => [s.id, s])),
    [bootstrap.data],
  )

  const levelLabel = (level: string) => {
    const key = `portfolio_level.${level.toLowerCase()}`
    const value = t(key)
    return value === key ? level : value
  }

  const childCount = useMemo(() => {
    const counts = new Map<string, number>()
    for (const node of nodes) if (node.parentId) counts.set(node.parentId, (counts.get(node.parentId) ?? 0) + 1)
    return counts
  }, [nodes])

  /** Aplica busca e chip antes de qualquer decisao de hierarquia. */
  const matched = useMemo(() => {
    const term = search.trim().toLowerCase()
    const statusCode = CHIP_STATUS[chip]
    return nodes.filter((node) => {
      if (term && !node.name.toLowerCase().includes(term) && !node.code.toLowerCase().includes(term)) return false
      if (statusCode) {
        const status = node.portfolioStatusId ? statusById.get(node.portfolioStatusId) : null
        if (status?.code !== statusCode) return false
      }
      return true
    })
  }, [nodes, search, chip, statusById])

  const filtrando = search.trim() !== '' || CHIP_STATUS[chip] !== undefined

  /**
   * Nós visíveis na árvore.
   *
   * Com filtro ativo o recolhimento e ignorado: esconder um resultado dentro de
   * um ramo fechado faria a busca parecer quebrada.
   */
  const visible = useMemo(() => {
    if (view === 'list' || filtrando) return matched
    const hidden = new Set<string>()
    return matched.filter((node) => {
      if (node.parentId && hidden.has(node.parentId)) {
        hidden.add(node.id)
        return false
      }
      if (collapsed.has(node.id)) hidden.add(node.id)
      return true
    })
  }, [matched, view, filtrando, collapsed])

  useExplorerConfig({
    footer: tree.isPending
      ? null
      : `${matched.length} ${matched.length === 1 ? 'item' : 'itens'} · ${LEVEL_ORDER.map(levelLabel).join(' → ')}`,
    onCreate,
    hasTreeView: true,
  })

  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <ExplorerBody>
      {tree.isPending ? (
        <div className="expl-skeleton" aria-busy>
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} height={26} />
          ))}
        </div>
      ) : tree.isError ? (
        <div className="expl-state">
          <i className="fas fa-exclamation-triangle" aria-hidden style={{ color: 'var(--ob-danger)' }} />
          <span className="expl-state__title">Não foi possível carregar o portfólio</span>
          <span className="expl-state__text">Verifique a conexão e tente novamente.</span>
        </div>
      ) : visible.length === 0 ? (
        <div className="expl-state">
          <i className="fas fa-layer-group" aria-hidden style={{ color: 'var(--ob-primary)' }} />
          <span className="expl-state__title">{filtrando ? 'Nenhum item encontrado' : 'Portfólio vazio'}</span>
          <span className="expl-state__text">
            {filtrando
              ? 'Ajuste a busca ou o filtro do painel.'
              : 'Cadastre a primeira Solução para estruturar produtos, módulos, funcionalidades e ações.'}
          </span>
        </div>
      ) : (
        <div className="expl-tree" role="tree" aria-label="Portfólio">
          {visible.map((node) => {
            const filhos = childCount.get(node.id) ?? 0
            const recolhido = collapsed.has(node.id)
            const status = node.portfolioStatusId ? statusById.get(node.portfolioStatusId) : null
            // Na visão lista a hierarquia vira o rótulo do nível; sem indentação.
            const indent = view === 'tree' && !filtrando ? node.depth * 14 : 0
            return (
              <div
                key={node.id}
                role="treeitem"
                aria-selected={selectedId === node.id}
                aria-expanded={view === 'tree' && filhos > 0 ? !recolhido : undefined}
                className={`expl-node${selectedId === node.id ? ' expl-node--on' : ''}`}
                style={{ marginLeft: indent }}
              >
                {view === 'tree' && !filtrando ? (
                  <button
                    type="button"
                    className={`expl-caret${filhos === 0 ? ' expl-caret--leaf' : ''}`}
                    aria-label={recolhido ? `Expandir ${node.name}` : `Recolher ${node.name}`}
                    disabled={filhos === 0}
                    onClick={() => toggle(node.id)}
                  >
                    <i className={`fas fa-chevron-${recolhido ? 'right' : 'down'}`} aria-hidden />
                  </button>
                ) : null}

                <span className="expl-icon" aria-hidden>
                  <i className={LEVEL_ICON[node.level] ?? 'fas fa-cube'} />
                </span>

                <button type="button" className="expl-main" onClick={() => navigate(`/portfolio/${node.id}`)}>
                  <span className="expl-title">{node.name}</span>
                  <span className="expl-meta">
                    {levelLabel(node.level)} · {node.code}
                    {filhos > 0 ? ` · ${filhos} ${filhos === 1 ? 'filho' : 'filhos'}` : ''}
                  </span>
                </button>

                {status ? (
                  <Badge tone={status.tone as StatusTone}>
                    {(() => {
                      const value = t(status.nameTranslationKey)
                      return value === status.nameTranslationKey ? status.code : value
                    })()}
                  </Badge>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </ExplorerBody>
  )
}
